import { createServerFn } from "@tanstack/react-start";

// Market station reference data for the trading room. Two objective blocks:
// 1. Periodic performance of XAUUSD over standard horizons.
// 2. Calculated institutional price levels (Camarilla and Fibonacci pivots)
//    derived from the previous completed trading day.
//
// No engine parameters, thresholds or policy values are exposed here. Every
// value is a public arithmetic derivation any terminal would publish.

export interface PerformanceRow {
  /** Horizon label shown to the desk. */
  label: "1D" | "1W" | "1M" | "3M" | "6M" | "1Y";
  /** Percentage change from the reference close to the latest close. */
  changePct: number | null;
  /** Absolute change in USD per ounce. */
  changeUsd: number | null;
}

export interface PivotLevel {
  tag: string;
  value: number;
  kind: "resistance" | "pivot" | "support";
}

export interface MarketStation {
  reference: {
    /** Previous completed session high, low and close. */
    high: number | null;
    low: number | null;
    close: number | null;
    /** Session date, ISO yyyy-mm-dd. */
    date: string | null;
  };
  lastClose: number | null;
  performance: PerformanceRow[];
  camarilla: PivotLevel[];
  fibonacci: PivotLevel[];
}

type Daily = { date: string; high: number; low: number; close: number };

let cache: { at: number; series: Daily[] } | null = null;
const CACHE_MS = 10 * 60_000;

async function loadDailySeries(): Promise<Daily[]> {
  if (cache && Date.now() - cache.at < CACHE_MS) return cache.series;
  const apiKey = process.env["TWELVE_DATA_API_KEY"];
  if (!apiKey) return cache?.series ?? [];
  try {
    const res = await fetch(
      `https://api.twelvedata.com/time_series?symbol=XAU/USD&interval=1day&outputsize=400&apikey=${apiKey}`,
      { cache: "no-store" },
    );
    if (!res.ok) return cache?.series ?? [];
    const json = (await res.json()) as {
      values?: Array<{ datetime: string; high: string; low: string; close: string }>;
    };
    if (!json.values?.length) return cache?.series ?? [];
    // Provider returns newest first. Store oldest first.
    const series: Daily[] = json.values
      .map((v) => ({
        date: v.datetime,
        high: Number(v.high),
        low: Number(v.low),
        close: Number(v.close),
      }))
      .filter((d) => Number.isFinite(d.high) && Number.isFinite(d.low) && Number.isFinite(d.close))
      .reverse();
    cache = { at: Date.now(), series };
    return series;
  } catch {
    return cache?.series ?? [];
  }
}

/** Trading days back per horizon. Gold trades roughly 5 sessions a week. */
const HORIZONS: Array<{ label: PerformanceRow["label"]; sessions: number }> = [
  { label: "1D", sessions: 1 },
  { label: "1W", sessions: 5 },
  { label: "1M", sessions: 22 },
  { label: "3M", sessions: 65 },
  { label: "6M", sessions: 130 },
  { label: "1Y", sessions: 260 },
];

function camarilla(h: number, l: number, c: number): PivotLevel[] {
  const r = h - l;
  const rows: PivotLevel[] = [
    { tag: "R4", value: c + r * 1.1 * 0.5, kind: "resistance" },
    { tag: "R3", value: c + r * 1.1 * 0.25, kind: "resistance" },
    { tag: "R2", value: c + r * 1.1 * (1 / 6), kind: "resistance" },
    { tag: "R1", value: c + r * 1.1 * (1 / 12), kind: "resistance" },
    { tag: "PP", value: (h + l + c) / 3, kind: "pivot" },
    { tag: "S1", value: c - r * 1.1 * (1 / 12), kind: "support" },
    { tag: "S2", value: c - r * 1.1 * (1 / 6), kind: "support" },
    { tag: "S3", value: c - r * 1.1 * 0.25, kind: "support" },
    { tag: "S4", value: c - r * 1.1 * 0.5, kind: "support" },
  ];
  return rows.map((x) => ({ ...x, value: Number(x.value.toFixed(2)) }));
}

function fibonacciPivots(h: number, l: number, c: number): PivotLevel[] {
  const pp = (h + l + c) / 3;
  const r = h - l;
  const rows: PivotLevel[] = [
    { tag: "R3", value: pp + r * 1.0, kind: "resistance" },
    { tag: "R2", value: pp + r * 0.618, kind: "resistance" },
    { tag: "R1", value: pp + r * 0.382, kind: "resistance" },
    { tag: "PP", value: pp, kind: "pivot" },
    { tag: "S1", value: pp - r * 0.382, kind: "support" },
    { tag: "S2", value: pp - r * 0.618, kind: "support" },
    { tag: "S3", value: pp - r * 1.0, kind: "support" },
  ];
  return rows.map((x) => ({ ...x, value: Number(x.value.toFixed(2)) }));
}

export const getMarketStation = createServerFn({ method: "GET" }).handler(
  async (): Promise<MarketStation> => {
    const series = await loadDailySeries();
    if (series.length < 2) {
      return {
        reference: { high: null, low: null, close: null, date: null },
        lastClose: null,
        performance: HORIZONS.map((h) => ({ label: h.label, changePct: null, changeUsd: null })),
        camarilla: [],
        fibonacci: [],
      };
    }

    const latest = series[series.length - 1]!;
    const prev = series[series.length - 2]!;
    const lastClose = latest.close;

    const performance: PerformanceRow[] = HORIZONS.map(({ label, sessions }) => {
      const idx = series.length - 1 - sessions;
      const base = idx >= 0 ? series[idx]! : null;
      if (!base || !base.close) return { label, changePct: null, changeUsd: null };
      const changeUsd = lastClose - base.close;
      return {
        label,
        changeUsd: Number(changeUsd.toFixed(2)),
        changePct: Number(((changeUsd / base.close) * 100).toFixed(2)),
      };
    });

    // Pivots use the previous completed session, the standard desk convention.
    const ref = prev;

    return {
      reference: { high: ref.high, low: ref.low, close: ref.close, date: ref.date },
      lastClose,
      performance,
      camarilla: camarilla(ref.high, ref.low, ref.close),
      fibonacci: fibonacciPivots(ref.high, ref.low, ref.close),
    };
  },
);
